import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface CandidatDetail {
  id: string;
  nom: string;
  score: number;
  decision: string;
  created_at: string;
  model_used?: string;
  scores_criteres?: { [key: string]: number };
}

interface SelectedCandidatesData {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  candidats: CandidatDetail[];
}

interface GlobalSocieteData {
  societe_id: string;
  societe_nom: string;
  note_qualite_offre: number | null;
  note_prix: number | null;
  note_durabilite: number | null;
  note_references: number | null;
  note_qualite_ia?: number | null;
  poids_qualite_offre: number;
  poids_prix: number;
  poids_durabilite: number;
  poids_references: number;
  score_pondere: number;
}

interface AppelOffres {
  id: string;
  titre: string;
  reference: string;
}

export const useGlobalComparisonPdfExport = () => {
  // Radar chart drawing function
  const drawRadarChart = (
    doc: jsPDF,
    centerX: number,
    centerY: number,
    radius: number,
    criteriaLabels: string[],
    companiesData: { name: string; scores: number[]; color: [number, number, number] }[]
  ) => {
    if (criteriaLabels.length < 3) return;

    const angleStep = (2 * Math.PI) / criteriaLabels.length;
    const levels = 5;

    // Draw colored zone backgrounds
    // Green zone (70-100)
    doc.setFillColor(220, 252, 231);
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % criteriaLabels.length) * angleStep - Math.PI / 2;
      const outerRadius = radius;
      const innerRadius = radius * 0.7;
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        centerX + innerRadius * Math.cos(angle2), centerY + innerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Yellow zone (40-70)
    doc.setFillColor(254, 249, 195);
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % criteriaLabels.length) * angleStep - Math.PI / 2;
      const outerRadius = radius * 0.7;
      const innerRadius = radius * 0.4;
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        centerX + innerRadius * Math.cos(angle2), centerY + innerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Red zone (0-40)
    doc.setFillColor(254, 226, 226);
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % criteriaLabels.length) * angleStep - Math.PI / 2;
      const outerRadius = radius * 0.4;
      doc.triangle(
        centerX, centerY,
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Draw grid
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const points: [number, number][] = [];
      for (let i = 0; i < criteriaLabels.length; i++) {
        const angle = i * angleStep - Math.PI / 2;
        points.push([centerX + levelRadius * Math.cos(angle), centerY + levelRadius * Math.sin(angle)]);
      }
      for (let i = 0; i < points.length; i++) {
        const next = (i + 1) % points.length;
        doc.line(points[i][0], points[i][1], points[next][0], points[next][1]);
      }
    }

    // Draw axis lines
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      doc.line(centerX, centerY, centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle));
    }

    // Draw each company's data polygon
    for (const company of companiesData) {
      const dataPoints: [number, number][] = [];
      for (let i = 0; i < company.scores.length; i++) {
        const angle = i * angleStep - Math.PI / 2;
        const value = (company.scores[i] / 100) * radius;
        dataPoints.push([centerX + value * Math.cos(angle), centerY + value * Math.sin(angle)]);
      }

      // Draw outline
      doc.setDrawColor(...company.color);
      doc.setLineWidth(1.5);
      for (let i = 0; i < dataPoints.length; i++) {
        const next = (i + 1) % dataPoints.length;
        doc.line(dataPoints[i][0], dataPoints[i][1], dataPoints[next][0], dataPoints[next][1]);
      }

      // Draw data points
      doc.setFillColor(...company.color);
      for (const point of dataPoints) {
        doc.circle(point[0], point[1], 1.5, 'F');
      }
    }

    // Draw labels
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'bold');
    for (let i = 0; i < criteriaLabels.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 10;
      const labelX = centerX + labelRadius * Math.cos(angle);
      const labelY = centerY + labelRadius * Math.sin(angle);
      let align: 'left' | 'center' | 'right' = 'center';
      let offsetX = 0;
      if (Math.cos(angle) > 0.3) { align = 'left'; offsetX = 2; }
      else if (Math.cos(angle) < -0.3) { align = 'right'; offsetX = -2; }
      doc.text(criteriaLabels[i], labelX + offsetX, labelY + 1, { align });
    }

    // Draw scale labels
    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const scoreValue = (level / levels) * 100;
      doc.text(String(Math.round(scoreValue)), centerX + 2, centerY - levelRadius + 2);
    }
  };

  // Grouped bar chart drawing function
  const drawGroupedBarChart = (
    doc: jsPDF,
    startX: number,
    startY: number,
    width: number,
    height: number,
    criteriaLabels: string[],
    companiesData: { name: string; scores: number[]; color: [number, number, number] }[]
  ) => {
    // Calculate proper spacing - add margins on each side
    const leftMargin = 12;
    const rightMargin = 12;
    const effectiveWidth = width - leftMargin - rightMargin;
    const barGroupWidth = effectiveWidth / criteriaLabels.length;
    const maxBarsPerGroup = companiesData.length;
    const gapBetweenBars = 0.5;
    const totalGapsWidth = (maxBarsPerGroup - 1) * gapBetweenBars;
    const availableBarSpace = barGroupWidth * 0.85; // 85% of group for bars
    const barWidth = Math.min((availableBarSpace - totalGapsWidth) / maxBarsPerGroup, 6); // Max 6mm per bar
    const maxScore = 100;

    const effectiveStartX = startX + leftMargin;

    // Draw Y axis
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(effectiveStartX, startY, effectiveStartX, startY + height);
    doc.line(effectiveStartX, startY + height, effectiveStartX + effectiveWidth, startY + height);

    // Draw Y axis labels
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    for (let i = 0; i <= 5; i++) {
      const yPos = startY + height - (height * i / 5);
      doc.text(`${i * 20}%`, effectiveStartX - 8, yPos + 2, { align: 'right' });
      doc.setDrawColor(230, 230, 230);
      doc.line(effectiveStartX, yPos, effectiveStartX + effectiveWidth, yPos);
    }

    // Draw bars for each criteria
    criteriaLabels.forEach((criteria, criteriaIndex) => {
      const groupCenterX = effectiveStartX + (criteriaIndex * barGroupWidth) + barGroupWidth / 2;
      const totalBarsWidth = (maxBarsPerGroup * barWidth) + totalGapsWidth;
      const groupStartX = groupCenterX - totalBarsWidth / 2;
      
      companiesData.forEach((company, companyIndex) => {
        const barX = groupStartX + (companyIndex * (barWidth + gapBetweenBars)) + barWidth / 2;
        const barHeight = (company.scores[criteriaIndex] / maxScore) * height;
        const barY = startY + height - barHeight;

        doc.setFillColor(...company.color);
        doc.rect(barX - barWidth / 2, barY, barWidth, barHeight, 'F');

        // Score label on top of bar
        doc.setFontSize(6);
        doc.setTextColor(...company.color);
        doc.text(`${company.scores[criteriaIndex].toFixed(0)}`, barX, barY - 2, { align: 'center' });
      });

      // Criteria label at bottom
      doc.setFontSize(8);
      doc.setTextColor(60, 60, 60);
      doc.text(criteria, groupCenterX, startY + height + 10, { align: 'center' });
    });
  };

  const exportGlobalComparisonToPdf = async (
    appelOffre: AppelOffres,
    societesData: GlobalSocieteData[],
    selectedCompanyIds: string[],
    selectedCandidatesData?: SelectedCandidatesData[]
  ) => {
    const doc = new jsPDF("portrait", "mm", "a4");
    
    // Colors
    const primaryColor: [number, number, number] = [59, 130, 246];
    const textColor: [number, number, number] = [30, 41, 59];
    const mutedColor: [number, number, number] = [100, 116, 139];
    const companyColors: [number, number, number][] = [
      [59, 130, 246],   // blue
      [239, 68, 68],    // red
      [34, 197, 94],    // green
      [168, 85, 247],   // purple
      [249, 115, 22],   // orange
      [236, 72, 153],   // pink
      [20, 184, 166],   // teal
      [245, 158, 11],   // amber
      [99, 102, 241],   // indigo
      [107, 114, 128],  // gray
    ];
    
    let yPosition = 20;

    // Filter to selected companies
    const selectedSocietes = societesData.filter(s => selectedCompanyIds.includes(s.societe_id));
    const criteriaLabels = ["Qualité de l'offre", "Prix", "Durabilité", "Références"];

    // ========== PAGE 1: HEADER & STATS ==========
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(...primaryColor);
    doc.text("Rapport Comparatif Global", 105, yPosition, { align: "center" });
    yPosition += 10;
    
    // Subtitle with AO info
    doc.setFontSize(12);
    doc.setTextColor(...mutedColor);
    doc.text(`${appelOffre.reference} - ${appelOffre.titre}`, 105, yPosition, { align: "center" });
    yPosition += 8;
    
    doc.setFontSize(9);
    doc.text(`Généré le ${format(new Date(), "dd MMMM yyyy à HH:mm", { locale: fr })}`, 105, yPosition, { align: "center" });
    yPosition += 15;

    // Separator line
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;

    // ========== SUMMARY STATISTICS ==========
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("1. Statistiques Globales", 20, yPosition);
    yPosition += 10;

    const avgScore = selectedSocietes.length > 0 
      ? selectedSocietes.reduce((sum, s) => sum + s.score_pondere, 0) / selectedSocietes.length 
      : 0;
    const bestScore = selectedSocietes.length > 0 
      ? Math.max(...selectedSocietes.map(s => s.score_pondere))
      : 0;
    const bestCompany = selectedSocietes.find(s => s.score_pondere === bestScore)?.societe_nom || "-";

    const statsData = [
      ["Nombre de sociétés comparées", selectedSocietes.length.toString()],
      ["Score pondéré moyen", `${avgScore.toFixed(2)}/5`],
      ["Meilleur score pondéré", `${bestScore.toFixed(2)}/5`],
      ["Meilleure société", bestCompany],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Indicateur", "Valeur"]],
      body: statsData,
      theme: "striped",
      headStyles: { 
        fillColor: primaryColor, 
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 80, halign: "center", fontStyle: "bold" }
      },
      margin: { left: 25, right: 25 }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 15;

    // ========== RANKING TABLE (landscape) ==========
    doc.addPage("landscape");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("2. Classement des Sociétés", 20, yPosition);
    yPosition += 10;

    const sortedSocietes = [...selectedSocietes].sort((a, b) => b.score_pondere - a.score_pondere);

    // Build IA quality data map from selectedCandidatesData
    const iaDataMap = new Map<string, { score: number; cvCount: number }>();
    if (selectedCandidatesData) {
      selectedCandidatesData.forEach(sc => {
        if (sc.score_moyen > 0 && sc.nb_candidatures > 0) {
          // Convert score_moyen from 0-100 to 0-5 scale
          iaDataMap.set(sc.societe_id, {
            score: (sc.score_moyen / 100) * 5,
            cvCount: sc.nb_candidatures
          });
        }
      });
    }

    const rankingData = sortedSocietes.map((s, index) => {
      const iaData = iaDataMap.get(s.societe_id);
      const iaCell = iaData ? `${iaData.score.toFixed(2)}/5\n(${iaData.cvCount} CV)` : '-';
      return [
        `#${index + 1}`,
        s.societe_nom,
        iaCell,
        s.note_qualite_offre !== null ? `${s.note_qualite_offre}/5` : '-',
        s.note_prix !== null ? `${s.note_prix}/5` : '-',
        s.note_durabilite !== null ? `${s.note_durabilite}/5` : '-',
        s.note_references !== null ? `${s.note_references}/5` : '-',
        `${s.score_pondere.toFixed(2)}/5`,
      ];
    });

    autoTable(doc, {
      startY: yPosition,
      head: [["Rang", "Société", "Qualité IA", "Qualité", "Prix", "Durabilité", "Références", "Score Final"]],
      body: rankingData,
      theme: "grid",
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 20, halign: "center" },
        1: { cellWidth: 60 },
        2: { cellWidth: 35, halign: "center" },
        3: { cellWidth: 30, halign: "center" },
        4: { cellWidth: 30, halign: "center" },
        5: { cellWidth: 30, halign: "center" },
        6: { cellWidth: 30, halign: "center" },
        7: { cellWidth: 30, halign: "center", fontStyle: "bold" }
      },
      margin: { left: 15, right: 15 },
      didParseCell: (data) => {
        // Purple styling for Qualité IA column (index 2)
        if (data.column.index === 2) {
          if (data.section === 'head') {
            data.cell.styles.fillColor = [147, 51, 234] as [number, number, number];
          } else if (data.section === 'body') {
            data.cell.styles.textColor = [147, 51, 234] as [number, number, number];
          }
        }
      }
    });

    // ========== PAGE 2: HEATMAP ==========
    doc.addPage("landscape");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("3. Matrice de Chaleur Comparative", 20, yPosition);
    yPosition += 5;
    
    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Scores par critère pour chaque société (notes /5 converties en %)", 20, yPosition);
    yPosition += 10;

    // Build heatmap data
    const heatmapHead = ["Société", "Score Global", ...criteriaLabels];
    
    const heatmapData = sortedSocietes.map(s => {
      const qualiteScore = s.note_qualite_offre !== null ? (s.note_qualite_offre / 5) * 100 : null;
      const prixScore = s.note_prix !== null ? (s.note_prix / 5) * 100 : null;
      const durabiliteScore = s.note_durabilite !== null ? (s.note_durabilite / 5) * 100 : null;
      const referencesScore = s.note_references !== null ? (s.note_references / 5) * 100 : null;
      const globalScore = (s.score_pondere / 5) * 100;
      
      return [
        s.societe_nom.length > 20 ? s.societe_nom.substring(0, 20) + "..." : s.societe_nom,
        `${globalScore.toFixed(2)}%\n(${s.score_pondere.toFixed(2)}/5)`,
        qualiteScore !== null ? `${qualiteScore.toFixed(2)}%\n(${(s.note_qualite_offre ?? 0).toFixed(2)}/5)` : '-',
        prixScore !== null ? `${prixScore.toFixed(2)}%\n(${(s.note_prix ?? 0).toFixed(2)}/5)` : '-',
        durabiliteScore !== null ? `${durabiliteScore.toFixed(2)}%\n(${(s.note_durabilite ?? 0).toFixed(2)}/5)` : '-',
        referencesScore !== null ? `${referencesScore.toFixed(2)}%\n(${(s.note_references ?? 0).toFixed(2)}/5)` : '-',
      ];
    });

    const getHeatmapColor = (value: number): { fill: [number, number, number], text: [number, number, number] } => {
      if (value >= 80) return { fill: [34, 197, 94], text: [255, 255, 255] };
      if (value >= 60) return { fill: [74, 222, 128], text: [0, 0, 0] };
      if (value >= 40) return { fill: [250, 204, 21], text: [0, 0, 0] };
      if (value >= 20) return { fill: [251, 146, 60], text: [0, 0, 0] };
      return { fill: [248, 113, 113], text: [255, 255, 255] };
    };

    autoTable(doc, {
      startY: yPosition,
      head: [heatmapHead],
      body: heatmapData,
      theme: "grid",
      headStyles: { 
        fillColor: primaryColor, 
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
        cellPadding: 3
      },
      styles: { fontSize: 9, cellPadding: 4, halign: "center", valign: "middle" },
      columnStyles: {
        0: { cellWidth: 50, halign: "left" },
      },
      margin: { left: 15, right: 15 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index > 0) {
          const cellText = data.cell.raw as string;
          if (cellText === '-') return;
          const match = cellText.match(/^([\d.]+)%/);
          const value = match ? parseFloat(match[1]) : 0;
          const colors = getHeatmapColor(value);
          data.cell.styles.fillColor = colors.fill;
          data.cell.styles.textColor = colors.text;
        }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;

    // Heatmap legend
    doc.setFontSize(8);
    doc.setTextColor(...textColor);
    doc.text("Légende:", 20, yPosition);
    
    const legendColors = [
      { label: "0-20%", color: [248, 113, 113] as [number, number, number] },
      { label: "20-40%", color: [251, 146, 60] as [number, number, number] },
      { label: "40-60%", color: [250, 204, 21] as [number, number, number] },
      { label: "60-80%", color: [74, 222, 128] as [number, number, number] },
      { label: "80-100%", color: [34, 197, 94] as [number, number, number] },
    ];
    
    let legendX = 45;
    legendColors.forEach(legend => {
      doc.setFillColor(...legend.color);
      doc.rect(legendX, yPosition - 3, 8, 4, 'F');
      doc.text(legend.label, legendX + 10, yPosition);
      legendX += 35;
    });

    // ========== PAGE 3: RADAR CHART ==========
    doc.addPage("portrait");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text(`4. Comparaison Radar (${selectedSocietes.length} Sociétés)`, 20, yPosition);
    yPosition += 5;

    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Visualisation comparative des scores par critère", 20, yPosition);
    yPosition += 12;

    // Prepare data for radar
    const companiesData = sortedSocietes.slice(0, 10).map((s, index) => ({
      name: s.societe_nom,
      scores: [
        s.note_qualite_offre !== null ? (s.note_qualite_offre / 5) * 100 : 0,
        s.note_prix !== null ? (s.note_prix / 5) * 100 : 0,
        s.note_durabilite !== null ? (s.note_durabilite / 5) * 100 : 0,
        s.note_references !== null ? (s.note_references / 5) * 100 : 0,
      ],
      color: companyColors[index % companyColors.length]
    }));

    // Draw radar chart
    drawRadarChart(doc, 105, yPosition + 60, 50, criteriaLabels, companiesData);
    yPosition += 130;

    // Legend for radar
    doc.setFontSize(9);
    doc.setTextColor(...textColor);
    doc.text("Légende des sociétés:", 20, yPosition);
    yPosition += 6;

    companiesData.forEach((company, index) => {
      if (index % 2 === 0) {
        doc.setFillColor(...company.color);
        doc.rect(20, yPosition - 2, 4, 4, 'F');
        doc.setTextColor(...textColor);
        doc.text(company.name.length > 25 ? company.name.substring(0, 25) + '...' : company.name, 26, yPosition + 1);
      } else {
        doc.setFillColor(...company.color);
        doc.rect(105, yPosition - 2 - 6, 4, 4, 'F');
        doc.setTextColor(...textColor);
        doc.text(company.name.length > 25 ? company.name.substring(0, 25) + '...' : company.name, 111, yPosition + 1 - 6);
      }
      if (index % 2 === 1) yPosition += 6;
    });
    if (companiesData.length % 2 === 1) yPosition += 6;

    // ========== PAGE 4: GROUPED BAR CHART ==========
    doc.addPage("landscape");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text("5. Comparaison par Barres Groupées", 20, yPosition);
    yPosition += 5;

    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Scores par critère pour chaque société sélectionnée", 20, yPosition);
    yPosition += 12;

    // Draw grouped bar chart - compact size to show all companies and values
    // Center the chart: landscape page width is 297mm
    const chartWidth = 160;
    const chartHeight = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const chartStartX = (pageWidth - chartWidth) / 2;
    drawGroupedBarChart(doc, chartStartX, yPosition, chartWidth, chartHeight, criteriaLabels, companiesData);
    yPosition += 70;

    // Legend for bar chart
    doc.setFontSize(8);
    doc.setTextColor(...textColor);
    doc.text("Légende:", 20, yPosition);
    yPosition += 6;

    let legendXBar = 20;
    companiesData.forEach((company, index) => {
      doc.setFillColor(...company.color);
      doc.rect(legendXBar, yPosition - 2, 4, 4, 'F');
      doc.setTextColor(...textColor);
      const shortName = company.name.length > 15 ? company.name.substring(0, 15) + '...' : company.name;
      doc.text(shortName, legendXBar + 6, yPosition + 1);
      legendXBar += 50;
      if (legendXBar > 250) {
        legendXBar = 20;
        yPosition += 8;
      }
    });

    // ========== PAGE 5: CLASSEMENT GLOBAL ==========
    doc.addPage("landscape");
    yPosition = 20;

    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text(`6. Classement Global (${selectedSocietes.length} Sociétés)`, 20, yPosition);
    yPosition += 5;

    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Tableau récapitulatif des rangs par critère et score final pondéré", 20, yPosition);
    yPosition += 12;

    // Calculate rankings per criteria
    const sortedByQualite = [...sortedSocietes]
      .filter(s => s.note_qualite_offre !== null)
      .sort((a, b) => (b.note_qualite_offre ?? 0) - (a.note_qualite_offre ?? 0));
    
    const sortedByPrix = [...sortedSocietes]
      .filter(s => s.note_prix !== null)
      .sort((a, b) => (b.note_prix ?? 0) - (a.note_prix ?? 0));
    
    const sortedByDurabilite = [...sortedSocietes]
      .filter(s => s.note_durabilite !== null)
      .sort((a, b) => (b.note_durabilite ?? 0) - (a.note_durabilite ?? 0));
    
    const sortedByReferences = [...sortedSocietes]
      .filter(s => s.note_references !== null)
      .sort((a, b) => (b.note_references ?? 0) - (a.note_references ?? 0));

    const qualiteRanks = new Map(sortedByQualite.map((s, i) => [s.societe_id, i + 1]));
    const prixRanks = new Map(sortedByPrix.map((s, i) => [s.societe_id, i + 1]));
    const durabiliteRanks = new Map(sortedByDurabilite.map((s, i) => [s.societe_id, i + 1]));
    const referencesRanks = new Map(sortedByReferences.map((s, i) => [s.societe_id, i + 1]));

    // Function to format rank with medal symbols
    const formatRank = (rank: number | undefined) => {
      if (rank === undefined) return '-';
      if (rank === 1) return '[OR] 1er';
      if (rank === 2) return '[AG] 2eme';
      if (rank === 3) return '[BR] 3eme';
      return `${rank}eme`;
    };

    // Function to format global rank with medal
    const formatGlobalRank = (rank: number) => {
      if (rank === 1) return '[OR] #1';
      if (rank === 2) return '[AG] #2';
      if (rank === 3) return '[BR] #3';
      return `#${rank}`;
    };

    // Create ranking table data
    const rankingTableData = sortedSocietes.map((s, index) => [
      formatGlobalRank(index + 1),
      s.societe_nom.length > 20 ? s.societe_nom.substring(0, 20) + '...' : s.societe_nom,
      `${s.score_pondere.toFixed(2)}/5`,
      s.note_qualite_offre !== null ? `${s.note_qualite_offre}/5 (${formatRank(qualiteRanks.get(s.societe_id))})` : '-',
      s.note_prix !== null ? `${s.note_prix}/5 (${formatRank(prixRanks.get(s.societe_id))})` : '-',
      s.note_durabilite !== null ? `${s.note_durabilite}/5 (${formatRank(durabiliteRanks.get(s.societe_id))})` : '-',
      s.note_references !== null ? `${s.note_references}/5 (${formatRank(referencesRanks.get(s.societe_id))})` : '-',
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Rang', 'Société', 'Score Final', 'Qualité Offre', 'Prix', 'Durabilité', 'Références']],
      body: rankingTableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 50 },
        2: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 45, halign: 'center' },
        4: { cellWidth: 40, halign: 'center' },
        5: { cellWidth: 40, halign: 'center' },
        6: { cellWidth: 40, halign: 'center' },
      },
      didParseCell: (data) => {
        // Highlight top 3 rows with medal colors
        if (data.section === 'body' && data.row.index < 3) {
          if (data.row.index === 0) {
            data.cell.styles.fillColor = [255, 215, 0] as [number, number, number]; // Gold
            data.cell.styles.textColor = [0, 0, 0] as [number, number, number];
          } else if (data.row.index === 1) {
            data.cell.styles.fillColor = [192, 192, 192] as [number, number, number]; // Silver
            data.cell.styles.textColor = [0, 0, 0] as [number, number, number];
          } else if (data.row.index === 2) {
            data.cell.styles.fillColor = [205, 127, 50] as [number, number, number]; // Bronze
            data.cell.styles.textColor = [255, 255, 255] as [number, number, number];
          }
        }
        // Highlight medal ranks in criteria columns
        if (data.section === 'body' && data.column.index >= 3) {
          const cellText = String(data.cell.text);
          if (cellText.includes('[OR]')) {
            data.cell.styles.textColor = [180, 140, 0] as [number, number, number];
            data.cell.styles.fontStyle = 'bold';
          } else if (cellText.includes('[AG]')) {
            data.cell.styles.textColor = [128, 128, 128] as [number, number, number];
            data.cell.styles.fontStyle = 'bold';
          } else if (cellText.includes('[BR]')) {
            data.cell.styles.textColor = [150, 90, 30] as [number, number, number];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // ========== PAGE 6: ANALYSES CV SÉLECTIONNÉES ==========
    if (selectedCandidatesData && selectedCandidatesData.length > 0) {
      doc.addPage("landscape");
      yPosition = 20;

      // Collect all selected candidates
      const allCandidates: Array<{
        societe: string;
        nom: string;
        score: number;
        decision: string;
        model_used?: string;
      }> = [];

      selectedCandidatesData.forEach(societe => {
        societe.candidats.forEach(candidat => {
          allCandidates.push({
            societe: societe.societe_nom,
            nom: candidat.nom,
            score: candidat.score,
            decision: candidat.decision,
            model_used: candidat.model_used
          });
        });
      });

      // Sort by score descending
      allCandidates.sort((a, b) => b.score - a.score);

      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text(`7. Analyses CV Sélectionnées (${allCandidates.length} candidats)`, 20, yPosition);
      yPosition += 5;

      doc.setFontSize(9);
      doc.setTextColor(...mutedColor);
      doc.text("Détail des analyses de CV sélectionnées dans l'onglet Classement", 20, yPosition);
      yPosition += 12;

      // Create table data
      const cvTableData = allCandidates.map((candidat, index) => {
        let decisionLabel = 'En attente';
        if (candidat.decision === 'shortlist') decisionLabel = 'Shortlist';
        else if (candidat.decision === 'consider') decisionLabel = 'Consider';
        else if (candidat.decision === 'reject') decisionLabel = 'Reject';

        return [
          `#${index + 1}`,
          candidat.societe.length > 25 ? candidat.societe.substring(0, 25) + '...' : candidat.societe,
          candidat.nom.length > 35 ? candidat.nom.substring(0, 35) + '...' : candidat.nom,
          `${candidat.score.toFixed(1)}%`,
          decisionLabel,
          candidat.model_used || '-'
        ];
      });

      autoTable(doc, {
        startY: yPosition,
        head: [['Rang', 'Société', 'Fichier CV', 'Score', 'Décision', 'Modèle LLM']],
        body: cvTableData,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: {
          fillColor: primaryColor,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 50 },
          2: { cellWidth: 70 },
          3: { cellWidth: 25, halign: 'center', fontStyle: 'bold' },
          4: { cellWidth: 30, halign: 'center' },
          5: { cellWidth: 45, halign: 'center' },
        },
        didParseCell: (data) => {
          // Top 3 highlighting
          if (data.section === 'body' && data.row.index < 3) {
            if (data.row.index === 0) {
              data.cell.styles.fillColor = [255, 215, 0] as [number, number, number];
              data.cell.styles.textColor = [0, 0, 0] as [number, number, number];
            } else if (data.row.index === 1) {
              data.cell.styles.fillColor = [192, 192, 192] as [number, number, number];
              data.cell.styles.textColor = [0, 0, 0] as [number, number, number];
            } else if (data.row.index === 2) {
              data.cell.styles.fillColor = [205, 127, 50] as [number, number, number];
              data.cell.styles.textColor = [255, 255, 255] as [number, number, number];
            }
          }
          // Decision coloring
          if (data.section === 'body' && data.column.index === 4) {
            const decision = String(data.cell.raw);
            if (decision === 'Shortlist') {
              data.cell.styles.textColor = [22, 163, 74] as [number, number, number];
              data.cell.styles.fontStyle = 'bold';
            } else if (decision === 'Consider') {
              data.cell.styles.textColor = [202, 138, 4] as [number, number, number];
              data.cell.styles.fontStyle = 'bold';
            } else if (decision === 'Reject') {
              data.cell.styles.textColor = [220, 38, 38] as [number, number, number];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });

      // Summary stats for CV analyses
      yPosition = (doc as any).lastAutoTable.finalY + 10;
      
      const shortlistCount = allCandidates.filter(c => c.decision === 'shortlist').length;
      const considerCount = allCandidates.filter(c => c.decision === 'consider').length;
      const rejectCount = allCandidates.filter(c => c.decision === 'reject').length;
      const avgCVScore = allCandidates.length > 0 
        ? allCandidates.reduce((sum, c) => sum + c.score, 0) / allCandidates.length 
        : 0;

      doc.setFontSize(10);
      doc.setTextColor(...textColor);
      doc.text('Résumé des analyses CV:', 20, yPosition);
      yPosition += 6;

      doc.setFontSize(9);
      doc.setTextColor(...mutedColor);
      doc.text(`• Total candidats sélectionnés: ${allCandidates.length}`, 25, yPosition);
      yPosition += 5;
      doc.text(`• Score moyen: ${avgCVScore.toFixed(1)}%`, 25, yPosition);
      yPosition += 5;
      doc.setTextColor(22, 163, 74);
      doc.text(`• Shortlist: ${shortlistCount}`, 25, yPosition);
      yPosition += 5;
      doc.setTextColor(202, 138, 4);
      doc.text(`• Consider: ${considerCount}`, 25, yPosition);
      yPosition += 5;
      doc.setTextColor(220, 38, 38);
      doc.text(`• Reject: ${rejectCount}`, 25, yPosition);
    }

    // ========== FOOTER ==========
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...mutedColor);
      doc.text(`Page ${i}/${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      doc.text(`${appelOffre.reference} - Vue Comparative Globale`, 20, doc.internal.pageSize.getHeight() - 10);
    }

    // Save
    doc.save(`comparaison-globale-${appelOffre.reference}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  return { exportGlobalComparisonToPdf };
};
